import firebase from "firebase";
import _ from "lodash";
import { toDayjs } from "../utils/date-helper";
let states = {};
const listeners = {};
// convert local date to firebase.firestore.Timestamp
const normalizeData = (data) => {
    for (const key in data) {
        if (data[key]) {
            if (data[key].seconds) {
                data[key] = firebase.firestore.Timestamp.fromDate(toDayjs(data[key]).toDate());
            }
            else if (data[key]._delegate?._methodName === "FieldValue.serverTimestamp") {
                data[key] = firebase.firestore.Timestamp.fromDate(new Date());
            }
            else if (data[key] instanceof Date) {
                data[key] = firebase.firestore.Timestamp.fromDate(data[key]);
            }
        }
    }
    return data;
};
class LocalFirestoreDocumentData {
    ref;
    id;
    exists;
    doc;
    constructor(ref, id, doc) {
        this.ref = ref;
        this.id = id;
        this.doc = normalizeData(doc);
        this.exists = Object.keys(this.doc ?? {}).length > 0;
    }
    data() {
        return this.doc;
    }
}
class LocalFirestoreBatch {
    set(ref, data) {
        ref.set(data);
    }
    update(ref, data) {
        ref.update(data);
    }
    delete(ref) {
        ref.delete();
    }
    async commit() {
        await Promise.resolve();
    }
}
const handleChange = _.debounce(() => {
    Object.values(listeners).forEach((listener) => listener());
}, 500);
const convertNestedRefToData = async (data) => {
    const copy = { ...data };
    const keys = Object.keys(copy);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (copy[key] instanceof LocalFirestoreDocumentReference) {
            const snapshot = await copy[key].get();
            copy[key] = snapshot.data();
        }
    }
    return copy;
};
class StateChangeObserver {
    notifyStateChange = handleChange;
    static addListener(key, handler) {
        listeners[key] = handler;
    }
    static removeListener(key) {
        listeners[key] = undefined;
    }
}
export class LocalFirestoreCollectionReference extends StateChangeObserver {
    collection;
    conditions = [];
    _limit;
    parent;
    constructor(collection, parent) {
        super();
        this.collection = collection;
        this.parent = parent;
    }
    getIdentifier() {
        if (!this.parent) {
            return this.collection;
        }
        return [this.parent.getIdentifier(), this.collection].join("/");
    }
    doc(id) {
        return new LocalFirestoreDocumentReference(new LocalFirestoreCollectionReference(this.collection, this.parent), id);
    }
    where(field, operator, value) {
        this.conditions.push({
            field,
            operator,
            value,
        });
        return this;
    }
    limit(limit) {
        this._limit = limit;
        return this;
    }
    orderBy() {
        return this;
    }
    getState() {
        if (!this.parent) {
            if (!states[this.collection]) {
                states[this.collection] = {};
            }
            return states[this.collection];
        }
        if (!this.parent.getState()[this.collection]) {
            this.parent.getState()[this.collection] = {};
        }
        return this.parent.getState()[this.collection];
    }
    getCollection() {
        const collection = this.getState();
        const docs = Object.keys(collection)
            .map((id) => {
            const ref = new LocalFirestoreDocumentReference(new LocalFirestoreCollectionReference(this.collection, this.parent), id);
            return new LocalFirestoreDocumentData(ref, id, collection[id].data);
        })
            .filter((snapshot) => {
            if (!this.conditions || !this.conditions.length) {
                return true;
            }
            return this.conditions.every((condition) => {
                if (!condition.field || !condition.operator || !condition.value) {
                    return true;
                }
                const { field, operator, value } = condition;
                switch (operator) {
                    case "==":
                        return snapshot.doc[field] === value;
                    case "!=":
                        return snapshot.doc[field] !== value;
                    case ">":
                        return snapshot.doc[field] > value;
                    case ">=":
                        return snapshot.doc[field] >= value;
                    case "<":
                        return snapshot.doc[field] < value;
                    case "<=":
                        return snapshot.doc[field] <= value;
                    default:
                        return true;
                }
            });
        });
        return {
            empty: docs.length === 0,
            docs: this._limit ? docs.slice(0, this._limit) : docs,
        };
    }
    async get() {
        return this.getCollection();
    }
}
export class LocalFirestoreDocumentReference extends StateChangeObserver {
    id;
    parent;
    constructor(parent, id) {
        super();
        this.parent = parent;
        this.id = id;
    }
    getIdentifier() {
        if (!this.parent) {
            return this.id;
        }
        return [this.parent.getIdentifier(), this.id].join("/");
    }
    collection(name) {
        return new LocalFirestoreCollectionReference(name, new LocalFirestoreDocumentReference(this.parent, this.id));
    }
    getState() {
        if (!this.parent.getState()[this.id]) {
            this.parent.getState()[this.id] = {};
        }
        return this.parent.getState()[this.id];
    }
    setState(state, merge = false) {
        const normalizedState = normalizeData(state);
        let currentState = this.parent.getState()[this.id];
        if (!currentState) {
            currentState = {};
            this.parent.getState()[this.id] = currentState;
        }
        if (merge) {
            currentState.data = {
                ...(currentState.data ?? {}),
                ...normalizedState,
            };
        }
        else {
            currentState.data = normalizedState;
        }
        this.notifyStateChange();
    }
    deleteState() {
        const currentState = this.parent.getState()[this.id];
        if (!currentState) {
            return;
        }
        if (!currentState.data) {
            return;
        }
        delete currentState.data;
        this.notifyStateChange();
    }
    getDocument() {
        return new LocalFirestoreDocumentData(this, this.id, this.getState().data);
    }
    async get() {
        return this.getDocument();
    }
    async set(data, merge = false) {
        this.setState(data, merge);
    }
    async update(data) {
        return await this.set(data, true);
    }
    async delete() {
        return this.deleteState();
    }
}
export class LocalFirestore {
    localBatch;
    static reset() {
        states = {};
        // @ts-expect-error states doesn't exist in document
        document.states = states;
    }
    static collection(name) {
        return new LocalFirestoreCollectionReference(name);
    }
    async batch() {
        this.localBatch = new LocalFirestoreBatch();
    }
}
export const installHooks = (instance) => {
    instance.bindedProps = [];
    instance.$bind = function (prop, ref) {
        StateChangeObserver.removeListener(prop);
        StateChangeObserver.addListener(prop, async function () {
            if (ref instanceof LocalFirestoreDocumentReference) {
                const snapshot = await ref.get();
                const data = await convertNestedRefToData(snapshot.data());
                instance.$set(instance, prop, {
                    id: snapshot.id,
                    ...data,
                });
            }
            else {
                const snapshot = await ref.get();
                const items = [];
                for (let k = 0; k < snapshot.docs.length; k++) {
                    const d = snapshot.docs[k];
                    const data = await convertNestedRefToData(d.data());
                    items.push({ id: d.id, ...data });
                }
                instance.$set(instance, prop, items);
            }
        });
        instance.bindedProps.push(prop);
        ref.notifyStateChange();
    };
};
export const uninstallHooks = (instance) => {
    instance.bindedProps.forEach((prop) => StateChangeObserver.removeListener(prop));
    instance.bindedProps = [];
    instance.$bind = undefined;
};
